import { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return "—"; } };

/**
 * Jun-2026 (User-Wunsch) — Interne Rechnungen (zu Auslieferungsaufträgen) zur Prüfung.
 * Listet interne Abrechnungen, bei denen der Mehrbetrag (netto, abgerechnet − gelistet)
 * die Schwelle (Standard 100 € netto, hier änderbar → andere Filterung) übersteigt.
 */
export default function InternalInvoicesTab() {
    const [items, setItems] = useState([]);
    const [threshold, setThreshold] = useState(100);
    const [thresholdInput, setThresholdInput] = useState("100");
    const [loading, setLoading] = useState(true);
    const [savingThr, setSavingThr] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/buchhaltung/internal-invoices");
            setItems(r.data?.items || []);
            const thr = Number(r.data?.threshold_net ?? 100);
            setThreshold(thr);
            setThresholdInput(String(thr));
        } catch {
            toast.error("Interne Rechnungen konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Jun-2026 (User-Wunsch) — nur Vorgänge MIT Rechnungsnummer anzeigen.
    const visibleItems = items.filter((it) => (it.internal_invoice_number || "").toString().trim());

    const saveThreshold = async () => {
        const v = parseFloat(thresholdInput);
        if (isNaN(v) || v < 0) { toast.error("Bitte einen gültigen Schwellenwert (≥ 0) angeben"); return; }
        setSavingThr(true);
        try {
            await api.put("/buchhaltung/internal-invoices/threshold", { threshold_net: v });
            toast.success("Schwelle gespeichert — Filterung aktualisiert");
            await load();
        } catch {
            toast.error("Schwelle konnte nicht gespeichert werden");
        } finally { setSavingThr(false); }
    };

    const markReviewed = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/buchhaltung/internal-invoices/${id}/mark-reviewed`);
            toast.success("Interne Rechnung als geprüft markiert");
            await load();
        } catch {
            toast.error("Konnte nicht als geprüft markiert werden");
        } finally { setBusyId(null); }
    };

    return (
        <div data-testid="buchhaltung-internal-invoices">
            {/* Schwellen-Einstellung */}
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4">
                <div className="flex items-start gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                    <p className="text-sm text-amber-900">
                        Bei <strong>automatischer Abrechnung interner Auslieferungsaufträge</strong> werden Aufträge gelistet, bei denen <strong>mehr abgerechnet wurde als ursprünglich im Auftrag gelistet</strong>. Übersteigt der <strong>Mehrbetrag (netto)</strong> die Schwelle, stehen sie hier zur Prüfung.
                    </p>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Schwelle Mehrbetrag (€ netto)</label>
                        <input type="number" min="0" step="1" value={thresholdInput}
                            onChange={(e) => setThresholdInput(e.target.value)}
                            className="block mt-1 h-9 w-40 rounded-sm border border-amber-300 px-3 text-sm font-mono-num"
                            data-testid="internal-inv-threshold-input" />
                    </div>
                    <button type="button" onClick={saveThreshold} disabled={savingThr}
                        className="h-9 px-4 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] text-sm font-bold inline-flex items-center gap-1.5"
                        data-testid="internal-inv-threshold-save">
                        {savingThr ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schwelle anwenden"}
                    </button>
                    <span className="text-xs text-amber-700">Aktiv: Mehrbetrag &gt; <b className="font-mono-num">{fmtEur(threshold)}</b></span>
                    <button type="button" onClick={load} className="ml-auto text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="internal-inv-refresh">
                        <RefreshCw className="w-3 h-3" /> Aktualisieren
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-gray-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Lade interne Rechnungen…</div>
            ) : visibleItems.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500" data-testid="internal-inv-empty">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    Keine internen Rechnungen (mit Rechnungsnummer) mit einem Mehrbetrag über {fmtEur(threshold)} zur Prüfung.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2 w-8"></th>
                                <th className="px-3 py-2">Interne Rechnung</th>
                                <th className="px-3 py-2">Auftrag</th>
                                <th className="px-3 py-2">Fahrzeug</th>
                                <th className="px-3 py-2 text-right">Gelistet (netto)</th>
                                <th className="px-3 py-2 text-right">Abgerechnet (netto)</th>
                                <th className="px-3 py-2 text-right">Mehrbetrag</th>
                                <th className="px-3 py-2">Datum</th>
                                <th className="px-3 py-2 text-right">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleItems.map((it) => {
                                const open = expandedId === it.id;
                                return (
                                <>
                                <tr key={it.id} className="border-t border-gray-100" data-testid={`internal-inv-row-${it.id}`}>
                                    <td className="px-3 py-2">
                                        <button type="button" onClick={() => setExpandedId(open ? null : it.id)}
                                            className="text-gray-500 hover:text-[#111827]" title="Soll-/Ist-Vergleich anzeigen"
                                            data-testid={`internal-inv-expand-${it.id}`}>
                                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                        </button>
                                    </td>
                                    <td className="px-3 py-2 font-bold font-mono-num">{it.internal_invoice_number}</td>
                                    <td className="px-3 py-2 text-gray-600 font-mono-num">{it.order_number}</td>
                                    <td className="px-3 py-2">{it.vehicle_label}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{fmtEur(it.listed_net)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{fmtEur(it.billed_net)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <span className="inline-flex items-center gap-1 font-bold font-mono-num text-red-600">
                                            <AlertTriangle className="w-3.5 h-3.5" /> +{fmtEur(it.excess_net)}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">{fmtDate(it.internal_invoiced_at)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button type="button" onClick={() => markReviewed(it.id)} disabled={busyId === it.id}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2.5 py-1 bg-white"
                                            data-testid={`internal-inv-review-${it.id}`}>
                                            {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5" /> Als geprüft markieren</>}
                                        </button>
                                    </td>
                                </tr>
                                {open && (
                                    <tr key={`${it.id}-detail`} className="bg-amber-50/40 border-t border-amber-100" data-testid={`internal-inv-detail-${it.id}`}>
                                        <td></td>
                                        <td colSpan={8} className="px-3 py-3">
                                            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800 mb-2">Soll-/Ist-Vergleich der Positionen</div>
                                            {(it.positions_diff || []).length === 0 ? (
                                                <div className="text-xs text-gray-500">Keine Positionsdaten verfügbar (Auftrag vor Einführung des Vergleichs angelegt).</div>
                                            ) : (
                                                <table className="w-full text-xs bg-white border border-amber-200 rounded-sm">
                                                    <thead>
                                                        <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-amber-100">
                                                            <th className="px-2 py-1.5">Position</th>
                                                            <th className="px-2 py-1.5 text-right">Soll (netto)</th>
                                                            <th className="px-2 py-1.5 text-right">Ist (netto)</th>
                                                            <th className="px-2 py-1.5 text-right">Differenz</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {it.positions_diff.map((p, i) => (
                                                            <tr key={i} className={`border-b border-gray-50 ${p.delta_net > 0 ? "bg-red-50/40" : ""}`}>
                                                                <td className="px-2 py-1.5">
                                                                    {p.description}
                                                                    {p.is_new && <span className="ml-1 text-[9px] font-bold uppercase bg-red-100 text-red-700 rounded-sm px-1 py-0.5">ungeplant</span>}
                                                                </td>
                                                                <td className="px-2 py-1.5 text-right font-mono-num text-gray-600">{fmtEur(p.listed_net)}</td>
                                                                <td className="px-2 py-1.5 text-right font-mono-num">{fmtEur(p.billed_net)}</td>
                                                                <td className={`px-2 py-1.5 text-right font-mono-num font-bold ${p.delta_net > 0 ? "text-red-600" : p.delta_net < 0 ? "text-emerald-600" : "text-gray-400"}`}>
                                                                    {p.delta_net > 0 ? "+" : ""}{fmtEur(p.delta_net)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                </>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
